<script setup>
// Gestão → Modo TV: a operação ao vivo, para ficar ligada numa TV. Visual
// próprio (escuro, o azul da Nexus Neural), independente do tema do painel.
//
// - lê o "ao vivo" a cada 4 s (mensagens novas, fila, equipe, destaque, alertas)
//   e o painel de hoje a cada minuto (ranking da equipe);
// - o centro gira entre conversas em tempo real, equipe e Nexus Neural;
// - modo discreto esconde o texto das mensagens; o som toca nos alertas novos;
// - pede para a tela não apagar (wake lock) e sai com Esc.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useRecursos } from '../nexus/recursos';
import MiniTeia from './MiniTeia.vue';
import { createGestaoApi, duracao, nota } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const router = useRouter();
const recursos = useRecursos();
const api = computed(() => createGestaoApi(props.accountId));

const EMOJI = { muito_satisfeito: '😄', satisfeito: '🙂', neutro: '😐', insatisfeito: '😟', irritado: '😡' };
const LEITURA = 4000;
const DURACAO_DA_VISTA = 20000;
const MAXIMO_NO_FEED = 25;

const vivo = ref(null);
const feed = ref([]);
const ranking = ref([]);
const discreto = ref(false);
const som = ref(false);
const agora = ref(Date.now());
const vistaAtual = ref(0);
const inicioDaVista = ref(Date.now());
const raiz = ref(null);
let ultima = 0;
let relogios = [];
let travaDaTela = null;
let alertasVistos = new Set();

const vistas = computed(() => ['FEED', 'RANKING', ...(recursos.value?.ia ? ['TEIA'] : [])]);
const vista = computed(() => vistas.value[vistaAtual.value % vistas.value.length]);
const progresso = computed(() => Math.min(100, ((agora.value - inicioDaVista.value) / DURACAO_DA_VISTA) * 100));
const mostra = indice => {
  vistaAtual.value = indice;
  inicioDaVista.value = Date.now();
};

const bipa = () => {
  try {
    const contexto = new (window.AudioContext || window.webkitAudioContext)();
    const oscilador = contexto.createOscillator();
    const ganho = contexto.createGain();
    oscilador.frequency.value = 880;
    ganho.gain.setValueAtTime(0.15, contexto.currentTime);
    ganho.gain.exponentialRampToValueAtTime(0.001, contexto.currentTime + 0.4);
    oscilador.connect(ganho).connect(contexto.destination);
    oscilador.start();
    oscilador.stop(contexto.currentTime + 0.4);
  } catch {
    // Navegador sem áudio: segue sem som.
  }
};

const chaveDoAlerta = alerta => `${alerta.tipo}-${alerta.cliente || alerta.quantos}`;
const confereAlertas = alertas => {
  const novos = alertas.filter(alerta => alerta.critico && !alertasVistos.has(chaveDoAlerta(alerta)));
  if (novos.length && som.value && alertasVistos.size) bipa();
  alertasVistos = new Set(alertas.map(chaveDoAlerta));
};

const leAoVivo = async () => {
  try {
    const { data } = await api.value.aoVivo({ depois: ultima || undefined });
    vivo.value = data;
    if (data.mensagens.length) {
      ultima = data.mensagens[data.mensagens.length - 1].id;
      feed.value = [...[...data.mensagens].reverse(), ...feed.value].slice(0, MAXIMO_NO_FEED);
    }
    confereAlertas(data.alertas);
  } catch {
    // Tenta de novo na próxima leitura.
  }
};

const leRanking = async () => {
  try {
    const { data } = await api.value.monitoramento({ dias: 0 });
    ranking.value = data.equipe.filter(linha => linha.resolvidas || linha.agora).slice(0, 8);
  } catch {
    // O ranking fica como está.
  }
};

const anda = () => {
  agora.value = Date.now();
  if (agora.value - inicioDaVista.value >= DURACAO_DA_VISTA) mostra(vistaAtual.value + 1);
};

const pedeTelaAcesa = async () => {
  try {
    travaDaTela = await navigator.wakeLock?.request('screen');
  } catch {
    travaDaTela = null;
  }
};
const telaCheia = () => raiz.value?.requestFullscreen?.().catch(() => {});
const sai = () => {
  if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  router.push({ name: 'neuxs_gestao_monitoramento', params: { accountId: props.accountId } });
};
const teclas = evento => {
  if (evento.key === 'Escape' && !document.fullscreenElement) sai();
};
// A trava some quando a aba fica escondida; volta quando ela aparece.
const aoVoltar = () => {
  if (!document.hidden) pedeTelaAcesa();
};

onMounted(() => {
  leAoVivo();
  leRanking();
  pedeTelaAcesa();
  relogios = [setInterval(leAoVivo, LEITURA), setInterval(leRanking, 60000), setInterval(anda, 1000)];
  window.addEventListener('keydown', teclas);
  document.addEventListener('visibilitychange', aoVoltar);
});
onBeforeUnmount(() => {
  relogios.forEach(clearInterval);
  window.removeEventListener('keydown', teclas);
  document.removeEventListener('visibilitychange', aoVoltar);
  travaDaTela?.release?.();
});

const hora = computed(() => new Date(agora.value).toLocaleTimeString('pt-BR'));
const espera = iso => duracao(Math.max(0, Math.round((agora.value - new Date(iso).getTime()) / 1000)));
const atrasado = iso => agora.value - new Date(iso).getTime() > (vivo.value?.sla_min || 5) * 60000;
const horaDa = iso => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const iniciais = nome => (nome || '').split(' ').map(parte => parte[0]).join('').slice(0, 2);
const textoDoAlerta = alerta => t(`NEUXS_GESTAO.TV.ALERTA.${alerta.tipo}`, { cliente: alerta.cliente, n: alerta.quantos });
const hoje = computed(() => vivo.value?.hoje || {});
</script>

<template>
  <div
    ref="raiz"
    class="fixed inset-0 z-50 flex flex-col gap-4 p-5 overflow-hidden text-[#dbe6f5] bg-[#070d1a]"
  >
    <header class="flex flex-wrap items-center gap-4">
      <h1 class="m-0 text-2xl font-semibold">{{ t('NEUXS_GESTAO.TV.TITULO') }}</h1>
      <span class="flex items-center gap-2 text-xs tracking-widest uppercase text-[#9cc8ff]">
        <span class="w-2 h-2 rounded-full bg-[#5aa9ff] animate-pulse" />
        {{ t('NEUXS_GESTAO.TV.AO_VIVO') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <button
          type="button"
          class="px-3 py-1.5 text-sm border rounded-lg"
          :class="discreto ? 'border-[#5aa9ff] text-[#9cc8ff] bg-[#5aa9ff]/15' : 'border-[#1a2b47] bg-[#0f1c33]'"
          @click="discreto = !discreto"
        >
          {{ t('NEUXS_GESTAO.TV.DISCRETO') }}
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-sm border rounded-lg"
          :class="som ? 'border-[#5aa9ff] text-[#9cc8ff] bg-[#5aa9ff]/15' : 'border-[#1a2b47] bg-[#0f1c33]'"
          @click="som = !som"
        >
          {{ t('NEUXS_GESTAO.TV.SOM') }}
        </button>
        <button type="button" class="px-3 py-1.5 text-sm border rounded-lg border-[#1a2b47] bg-[#0f1c33]" @click="telaCheia">
          {{ t('NEUXS_GESTAO.TV.TELA_CHEIA') }}
        </button>
        <button type="button" class="px-3 py-1.5 text-sm border rounded-lg border-[#1a2b47] bg-[#0f1c33]" @click="sai">
          {{ t('NEUXS_GESTAO.TV.SAIR') }}
        </button>
      </div>
      <span class="text-2xl ms-auto tabular-nums text-[#9cc8ff]">{{ hora }}</span>
    </header>

    <div class="grid grid-cols-3 gap-3 lg:grid-cols-6">
      <div class="px-4 py-3 border rounded-xl border-[#1a2b47] bg-[#0b1527]">
        <span class="text-xs tracking-wide uppercase text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.HOJE') }}</span>
        <strong class="block text-4xl font-normal tabular-nums">{{ hoje.conversas ?? '—' }}</strong>
      </div>
      <div class="px-4 py-3 border rounded-xl bg-[#0b1527]" :class="vivo?.alertas?.some(a => a.tipo === 'sla') ? 'border-[#d6a865]' : 'border-[#1a2b47]'">
        <span class="text-xs tracking-wide uppercase text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.NA_FILA') }}</span>
        <strong class="block text-4xl font-normal tabular-nums">{{ hoje.na_fila ?? '—' }}</strong>
      </div>
      <div class="px-4 py-3 border rounded-xl border-[#1a2b47] bg-[#0b1527]">
        <span class="text-xs tracking-wide uppercase text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.EQUIPE') }}</span>
        <strong class="block text-4xl font-normal tabular-nums">{{ hoje.com_a_equipe ?? '—' }}</strong>
      </div>
      <div class="px-4 py-3 border rounded-xl border-[#1a2b47] bg-[#0b1527]">
        <span class="text-xs tracking-wide uppercase text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.IA') }}</span>
        <strong class="block text-4xl font-normal tabular-nums">{{ hoje.com_a_ia ?? '—' }}</strong>
      </div>
      <div class="px-4 py-3 border rounded-xl border-[#1a2b47] bg-[#0b1527]">
        <span class="text-xs tracking-wide uppercase text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.IA_RESOLVEU') }}</span>
        <strong class="block text-4xl font-normal tabular-nums">{{ hoje.ia_resolveu === null || hoje.ia_resolveu === undefined ? '—' : `${hoje.ia_resolveu}%` }}</strong>
      </div>
      <div class="px-4 py-3 border rounded-xl border-[#1a2b47] bg-[#0b1527]">
        <span class="text-xs tracking-wide uppercase text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.SENTIMENTO') }}</span>
        <strong class="block text-4xl font-normal">{{ EMOJI[hoje.sentimento] || '—' }}</strong>
      </div>
    </div>

    <div class="grid flex-1 min-h-0 grid-cols-1 gap-4 lg:grid-cols-[1fr_1.7fr_1fr]">
      <div class="grid min-h-0 gap-4 grid-rows-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <section class="flex flex-col min-h-0 p-4 border rounded-xl border-[#1a2b47] bg-[#0b1527]">
          <h2 class="flex items-center m-0 mb-3 text-base font-semibold">
            {{ t('NEUXS_GESTAO.TV.FILA') }}
            <span class="text-xs font-normal ms-auto text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.FILA_SLA', { min: vivo?.sla_min || 5 }) }}</span>
          </h2>
          <p v-if="!vivo?.fila?.length" class="m-0 text-sm text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.FILA_VAZIA') }}</p>
          <div class="flex flex-col gap-1.5 overflow-hidden">
            <div
              v-for="item in vivo?.fila || []"
              :key="item.conversa"
              class="grid grid-cols-[1fr_auto] gap-x-3 px-3 py-2 rounded-md bg-[#0f1c33] border-s-[3px]"
              :class="atrasado(item.esperando_desde) ? 'border-[#e07a85]' : 'border-[#5aa9ff]'"
            >
              <span class="truncate">{{ item.cliente }} <span v-if="item.sentimento">{{ EMOJI[item.sentimento] }}</span></span>
              <span class="row-span-2 self-center text-lg tabular-nums" :class="atrasado(item.esperando_desde) ? 'text-[#e07a85]' : ''">
                {{ espera(item.esperando_desde) }}
              </span>
              <small class="text-[#7d91b0]">{{ item.time || '—' }}</small>
            </div>
          </div>
        </section>
        <section class="flex flex-col min-h-0 p-4 border rounded-xl border-[#1a2b47] bg-[#0b1527]">
          <h2 class="flex items-center m-0 mb-3 text-base font-semibold">
            {{ t('NEUXS_GESTAO.TV.ONLINE') }}
            <span class="text-xs font-normal ms-auto text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.ONLINE_DICA') }}</span>
          </h2>
          <p v-if="!vivo?.equipe?.length" class="m-0 text-sm text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.NINGUEM') }}</p>
          <div class="flex flex-col gap-2 overflow-hidden">
            <div v-for="pessoa in vivo?.equipe || []" :key="pessoa.id" class="grid grid-cols-[1fr_6rem_1.5rem] items-center gap-3 text-sm">
              <span class="truncate">
                <span class="inline-block w-2 h-2 rounded-full me-2" :class="pessoa.status === 'busy' ? 'bg-[#d6a865]' : 'bg-[#5cc8a8]'" />
                {{ pessoa.nome }}
              </span>
              <span class="flex gap-0.5">
                <i v-for="n in 8" :key="n" class="flex-1 h-2 rounded-sm" :class="n <= pessoa.agora ? 'bg-[#8aa3c9]' : 'bg-[#0f1c33]'" />
              </span>
              <span class="tabular-nums text-end">{{ pessoa.agora }}</span>
            </div>
          </div>
        </section>
      </div>

      <section class="flex flex-col min-h-0 p-4 border rounded-xl border-[#1a2b47] bg-[#0b1527]">
        <h2 class="flex items-center m-0 mb-3 text-base font-semibold">
          {{ t(`NEUXS_GESTAO.TV.VISTAS.${vista}`) }}
          <span v-if="vista === 'FEED'" class="text-xs font-normal ms-auto text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.MASCARADOS') }}</span>
        </h2>
        <div v-if="vista === 'FEED'" class="flex flex-col flex-1 min-h-0 gap-2 overflow-hidden">
          <p v-if="!feed.length" class="m-0 text-sm text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.SEM_MENSAGENS') }}</p>
          <TransitionGroup name="neuxs-feed">
            <div
              v-for="mensagem in feed"
              :key="mensagem.id"
              class="grid grid-cols-[2.25rem_1fr_auto] gap-x-3 gap-y-0.5 px-3 py-2.5 rounded-lg"
              :class="mensagem.autor === 'cliente' ? 'border border-[#1a2b47]' : 'bg-[#0f1c33]'"
            >
              <span
                class="grid row-span-2 text-sm border rounded-full place-items-center size-9 bg-[#0b1527]"
                :class="mensagem.autor === 'ia' ? 'border-[#5aa9ff] text-[#9cc8ff]' : 'border-[#1a2b47]'"
              >
                {{ mensagem.autor === 'cliente' ? '👤' : mensagem.autor === 'ia' ? '🤖' : iniciais(mensagem.nome) || '•' }}
              </span>
              <span class="text-xs truncate text-[#7d91b0]">
                <template v-if="mensagem.autor === 'cliente'">
                  <b class="font-semibold text-[#dbe6f5]">{{ mensagem.cliente }}</b> {{ t('NEUXS_GESTAO.TV.AUTORES.cliente') }}
                </template>
                <template v-else>
                  <b class="font-semibold text-[#dbe6f5]">
                    {{ mensagem.autor === 'ia' ? `IA${mensagem.nome ? ` · ${mensagem.nome}` : ''}` : mensagem.nome || t(`NEUXS_GESTAO.TV.AUTORES.${mensagem.autor}`) }}
                  </b>
                  {{ t('NEUXS_GESTAO.TV.PARA', { cliente: mensagem.cliente }) }}
                </template>
                <template v-if="mensagem.canal"> · {{ mensagem.canal }}</template>
              </span>
              <span class="row-span-2 self-center text-xs text-end text-[#7d91b0]">
                <span v-if="mensagem.sentimento" class="block text-xl">{{ EMOJI[mensagem.sentimento] }}</span>
                {{ horaDa(mensagem.em) }}
              </span>
              <span class="text-sm break-words" :class="discreto ? 'italic text-[#4d6080]' : ''">
                {{ discreto ? t('NEUXS_GESTAO.TV.OCULTA') : mensagem.texto }}
              </span>
            </div>
          </TransitionGroup>
        </div>
        <div v-else-if="vista === 'RANKING'" class="flex flex-col gap-2">
          <div
            v-for="(pessoa, i) in ranking"
            :key="pessoa.id"
            class="grid grid-cols-[2rem_1fr_5rem_5rem_4rem] items-center gap-3 px-3 py-2.5 text-base rounded-lg bg-[#0f1c33]"
          >
            <span class="text-lg text-[#9cc8ff]">{{ i + 1 }}</span>
            <span class="truncate">{{ pessoa.nome }}</span>
            <span class="text-end tabular-nums">{{ pessoa.resolvidas }}<small class="block text-[10px] uppercase text-[#7d91b0]">{{ t('NEUXS_GESTAO.MONITOR.RESOLVIDAS') }}</small></span>
            <span class="text-end tabular-nums">{{ duracao(pessoa.primeira_resposta) }}<small class="block text-[10px] uppercase text-[#7d91b0]">{{ t('NEUXS_GESTAO.MONITOR.PRIMEIRA') }}</small></span>
            <span class="text-end tabular-nums">{{ nota(pessoa.csat) }}<small class="block text-[10px] uppercase text-[#7d91b0]">{{ t('NEUXS_GESTAO.MONITOR.KPI.CSAT') }}</small></span>
          </div>
        </div>
        <div v-else class="flex-1 min-h-0">
          <MiniTeia :account-id="accountId" />
        </div>
      </section>

      <div class="grid min-h-0 gap-4 grid-rows-[auto_minmax(0,1fr)]">
        <section
          class="p-4 border rounded-xl bg-[#0b1527]"
          :class="vivo?.destaque ? 'border-[#e07a85]/60' : 'border-[#1a2b47]'"
        >
          <h2 class="flex items-center m-0 mb-2 text-base font-semibold">
            {{ t('NEUXS_GESTAO.TV.DESTAQUE') }}
            <span class="text-xs font-normal ms-auto text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.DESTAQUE_DICA') }}</span>
          </h2>
          <p v-if="!vivo?.destaque" class="m-0 text-sm text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.SEM_DESTAQUE') }}</p>
          <template v-else>
            <p class="m-0 text-lg">{{ vivo.destaque.cliente }} 😡</p>
            <p class="m-0 my-1 text-sm text-[#7d91b0]">
              {{ vivo.destaque.motivo }}<template v-if="vivo.destaque.time"> · {{ vivo.destaque.time }}</template>
              · {{ t('NEUXS_GESTAO.TV.ESPERA', { t: espera(vivo.destaque.esperando_desde) }) }}
            </p>
            <p class="flex items-center gap-1.5 m-0 text-2xl">
              <template v-for="(nivel, i) in vivo.destaque.trilha" :key="i">
                <span v-if="i" class="text-xs text-[#4d6080]">→</span>{{ EMOJI[nivel] }}
              </template>
            </p>
          </template>
        </section>
        <section class="flex flex-col min-h-0 p-4 border rounded-xl border-[#1a2b47] bg-[#0b1527]">
          <h2 class="m-0 mb-3 text-base font-semibold">{{ t('NEUXS_GESTAO.TV.ALERTAS') }}</h2>
          <p v-if="!vivo?.alertas?.length" class="m-0 text-sm text-[#7d91b0]">{{ t('NEUXS_GESTAO.TV.SEM_ALERTAS') }}</p>
          <div class="flex flex-col gap-2 overflow-hidden">
            <div v-for="(alerta, i) in vivo?.alertas || []" :key="i" class="flex items-center gap-3 px-3 py-2 text-sm rounded-md bg-[#0f1c33]">
              <span class="w-2 h-2 rounded-full shrink-0" :class="alerta.critico ? 'bg-[#e07a85]' : 'bg-[#d6a865]'" />
              <span class="flex-1">{{ textoDoAlerta(alerta) }}</span>
              <small v-if="alerta.em" class="tabular-nums text-[#7d91b0]">{{ horaDa(alerta.em) }}</small>
            </div>
          </div>
        </section>
      </div>
    </div>

    <footer class="flex flex-wrap items-center gap-3 text-xs text-[#7d91b0]">
      <button
        v-for="(nome, i) in vistas"
        :key="nome"
        type="button"
        class="px-2.5 py-0.5 border rounded-full"
        :class="vista === nome ? 'border-[#5aa9ff] text-[#9cc8ff]' : 'border-[#1a2b47]'"
        @click="mostra(i)"
      >
        {{ t(`NEUXS_GESTAO.TV.VISTAS.${nome}`) }}
      </button>
      <span class="flex-1 h-0.5 overflow-hidden rounded min-w-24 bg-[#0f1c33]">
        <span class="block h-full bg-[#5aa9ff]" :style="{ width: `${progresso}%` }" />
      </span>
      <span>{{ t('NEUXS_GESTAO.TV.RODAPE') }}</span>
    </footer>
  </div>
</template>

<style scoped>
.neuxs-feed-enter-active {
  transition: all 0.5s ease-out;
}
.neuxs-feed-enter-from {
  opacity: 0.2;
  transform: translateY(-14px);
}
@media (prefers-reduced-motion: reduce) {
  .neuxs-feed-enter-active {
    transition: none;
  }
}
</style>
